// Shared cart code ('@purolabs/cart'), used by the cart drawer, the cart page, quick add, the product form and the
// recommendation sections.
//
// - cartRequest(): one POST to /cart/add.js or /cart/change.js that also asks for the HTML of every cart view on the
//   page (the `sections` parameter), so an add or change is a single request. The full cart comes from the JSON the
//   cart drawer embeds in its section ([data-cart-json]); /cart.js is only fetched when no cart view is on the page.
// - announceCartChange(): wraps a request in Shopify's standard cart event (shopify:cart:lines-update), which the
//   header count, the cart views and apps listen to. Failures resolve with didError rather than rejecting.
// - CartView: the behaviour the drawer and the cart page share: quantity (typed, debounced, or +/-), remove, plan
//   change and the upgrade switch, per-line errors, a polite status message, focus after a removal, Horizon's morph to
//   swap in the new HTML, stale responses dropped, and a refresh on back/forward.
// - Recommendations: the loader behind every "recommended products" block.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

export const root = () => window.Shopify?.routes?.root || '/';

/**
 * Free gift tiers (assets/purolabs-gift-tiers.js) add and remove gifts after announced cart changes. Flows that change
 * the cart with their own requests and go straight to checkout (landing buy box, build a bundle, create-checkout
 * links) call this first. It waits for the gift script when tiers are on here (it loads at the end of the page), is
 * capped at 3s so checkout never waits long, and resolves at once when tiers are off.
 */
export async function syncGiftTiers() {
  const deadline = Date.now() + 3000;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  while (document.getElementById('pl-gift-tiers-config') && !window.purolabsGiftTiers && Date.now() < deadline) await wait(50);
  await Promise.race([window.purolabsGiftTiers?.sync(), wait(Math.max(0, deadline - Date.now()))]);
}

const GENERIC_ERROR = 'Sorry, something went wrong. Please try again.';
const NETWORK_ERROR = "We couldn't update your cart. Please check your connection and try again.";

/** Section ids of the cart views on this page (drawer, cart page), asked for in every cart request */
export function cartSectionIds() {
  return [...new Set([...document.querySelectorAll('[data-cart-view][data-section-id]')].map((view) => view.dataset.sectionId))];
}

class CartRequestError extends Error {
  constructor(message, { status = 0, data = null } = {}) {
    super(message);
    this.name = 'CartRequestError';
    this.status = status;
    this.data = data;
  }
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    // An HTML error page (or an empty body) instead of JSON
    return null;
  }
}

/** The whole cart, as /cart.js returns it, from the JSON the drawer embeds in its section */
function cartFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    const match = typeof html === 'string' && html.match(/<script type="application\/json" data-cart-json>([\s\S]*?)<\/script>/);
    if (!match) continue;
    try {
      const cart = JSON.parse(match[1]);
      // Liquid's cart object has no token or discount codes; Shopify's cart events expect both
      const cookie = document.cookie.match(/(?:^|; )cart=([^;]*)/);
      cart.token ??= cookie ? decodeURIComponent(cookie[1]) : undefined;
      cart.discount_codes ??= [];
      return cart;
    } catch {
      // fall through to the next section, then /cart.js
    }
  }
  return null;
}

export async function getCart(signal) {
  const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' }, signal });
  const cart = response.ok ? await readJson(response) : null;
  if (!cart) throw new CartRequestError(GENERIC_ERROR, { status: response.status });
  return cart;
}

/**
 * POSTs to cart/add.js or cart/change.js with every cart view's section HTML requested in the same call.
 * @param {'cart/add.js'|'cart/change.js'|'cart/update.js'} endpoint
 * @param {FormData|object} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ data: object, cart: object, sections: Record<string, string> }>}
 */
export async function cartRequest(endpoint, body, { signal } = {}) {
  const sectionIds = cartSectionIds();
  const isForm = body instanceof FormData;
  if (sectionIds.length) {
    if (isForm) {
      body.set('sections', sectionIds.join(','));
      body.set('sections_url', window.location.pathname);
    } else {
      body = { ...body, sections: sectionIds, sections_url: window.location.pathname };
    }
  }

  let response;
  try {
    response = await fetch(`${root()}${endpoint}`, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new CartRequestError(NETWORK_ERROR);
  }

  const data = await readJson(response);
  if (!response.ok || !data || data.status) {
    const message = (data && (data.description || data.message)) || GENERIC_ERROR;
    throw new CartRequestError(typeof message === 'string' ? message : GENERIC_ERROR, { status: response.status, data });
  }

  const sections = data.sections || {};
  const cart = Array.isArray(data.items) && 'item_count' in data ? data : cartFromSections(sections) || (await getCart(signal));
  return { data, cart, sections };
}

let sequence = 0;

/**
 * Announces a cart change with Shopify's standard event and runs it. Listeners get
 * { cart, detail: { source, items, sections, sequence } }, or detail.didError (with the error message) on failure.
 * @param {EventTarget} target - where the event is dispatched (it bubbles)
 * @param {{ action: 'add'|'update'|'remove', context?: string, lines: object[], source: string, handled?: boolean }} change
 * @param {(signal?: AbortSignal) => Promise<{ cart: object, sections: object }>} run
 */
export async function announceCartChange(target, { action, context = 'cart', lines, source, handled = false }, run) {
  const id = ++sequence;
  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));

  try {
    const result = await run();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart),
      detail: { source, items: result.cart.items, sections: result.sections, sequence: id },
    });
    return { ...result, sequence: id };
  } catch (error) {
    const aborted = error.name === 'AbortError';
    let cart = null;
    if (!aborted) cart = await getCart().catch(() => null);
    deferred.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail: { source, didError: true, aborted, handled, error: error.message, items: cart?.items, sequence: id },
    });
    throw error;
  }
}

/** Adds a form's (or FormData's) items to the cart: one request, announced with the standard event */
export function addToCart(target, formData, { source, handled = false, context = 'product' } = {}) {
  const lines = [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }];
  return announceCartChange(target, { action: 'add', context, lines, source, handled }, () => cartRequest('cart/add.js', formData));
}

/** Re-renders every cart view and the header count after the cart changed somewhere we didn't see (bfcache) */
export function refreshCart(source = 'pl-cart-refresh') {
  const ids = cartSectionIds();
  return announceCartChange(document, { action: 'update', lines: [], source }, async () => {
    if (!ids.length) return { cart: await getCart(), sections: {} };
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    const sections = response.ok ? await readJson(response) : null;
    if (!sections) throw new CartRequestError(GENERIC_ERROR, { status: response.status });
    const cart = cartFromSections(sections) || (await getCart());
    return { cart, sections };
  }).catch(() => {});
}

// Back/forward: the page comes back as it was (from the back/forward cache, or the HTTP cache), possibly with a stale
// cart, so the cart views and the header count are brought up to date
if (!window.purolabsCartPageshow) {
  window.purolabsCartPageshow = true;
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart();
  });
  if (performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') {
    const run = () => setTimeout(() => refreshCart(), 0);
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }
}

/** Swaps new server HTML into a container with Horizon's morph (keeps focus, images and in-progress input) */
export function morphInto(current, next) {
  if (!current || !next) return;
  // Morph drops whitespace-only text from the new HTML, so drop it from the old too: otherwise it lines the two up by
  // moving elements past that whitespace, and moving the element with focus (a quantity field) blurs it
  const walker = document.createTreeWalker(current, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.nodeValue.trim() === '' && !node.parentElement?.closest('pre, textarea') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  const blanks = [];
  while (walker.nextNode()) blanks.push(walker.currentNode);
  for (const node of blanks) node.remove();
  morph(current, next);
}

/**
 * A cart view (drawer or page). The section's markup provides:
 * [data-cart-view][data-section-id] on this element, [data-cart-content] (re-rendered), [data-cart-status] (polite
 * message, outside the content), [data-cart-subtotal="Label"], [data-cart-heading] (focus goes there when the cart empties),
 * and per line [data-line-key][data-title][data-quantity] holding a[data-quantity] buttons, [data-quantity-input],
 * [data-line-plan] select, [data-upgrade-plan] switch and [data-line-error].
 */
export class CartView extends HTMLElement {
  #rendered = 0;
  #busy = new Set();
  #queued = new Map();
  #timers = new Map();

  get sectionId() {
    return this.dataset.sectionId;
  }

  get content() {
    return this.querySelector('[data-cart-content]');
  }

  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    for (const timer of this.#timers.values()) clearTimeout(timer);
    this.#timers.clear();
    this.#queued.clear();
  }

  /** Hook for subclasses: called after new HTML was swapped in */
  updated() {}

  /** Hook for subclasses: another part of the page changed the cart */
  cartChanged() {}

  render(html, id = 0) {
    if (!html) return false;
    // Already shown (the change came from this view), or older than what's on screen
    if (id && id <= this.#rendered) return true;
    if (id) this.#rendered = id;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
    if (!next) return false;
    morphInto(this.content, next);
    this.updated();
    return true;
  }

  /** Fetches this section again through Horizon's section renderer (aborts an earlier refresh) */
  async refresh() {
    const id = ++sequence;
    this.refreshController?.abort();
    this.refreshController = new AbortController();
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, new URL(window.location.href), this.refreshController.signal);
      this.render(html, id);
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Cart:', error);
    }
  }

  #onCartUpdate = async (event) => {
    const result = await event.promise?.catch(() => null);
    if (!result || result.detail?.aborted) return;
    const { detail = {} } = result;
    const html = detail.sections?.[this.sectionId];
    if (html) this.render(html, detail.sequence);
    // A failed request changed nothing (and a refresh would wipe the line's error message)
    else if (!detail.didError) await this.refresh();
    this.cartChanged(event, result);
  };

  #onClick = (event) => {
    const quantityLink = event.target.closest('a[data-quantity]');
    if (quantityLink && this.contains(quantityLink)) {
      event.preventDefault();
      const line = quantityLink.closest('[data-line-key]');
      if (line) this.change(line, { quantity: Number(quantityLink.dataset.quantity) });
      return;
    }

    const upgrade = event.target.closest('[data-upgrade-plan]');
    if (upgrade) {
      const line = upgrade.closest('[data-line-key]');
      upgrade.setAttribute('aria-checked', 'true');
      this.change(line, { selling_plan: Number(upgrade.dataset.upgradePlan) }, { kind: 'upgrade', control: upgrade });
    }
  };

  // Typed quantities wait 300ms for the shopper to finish
  #onInput = (event) => {
    const input = event.target.closest('[data-quantity-input]');
    if (!input) return;
    const line = input.closest('[data-line-key]');
    input.setAttribute('data-skip-value-update', '');
    clearTimeout(this.#timers.get(line.id));
    this.#timers.set(line.id, setTimeout(() => this.#submitTyped(input), 300));
  };

  #onChange = (event) => {
    const input = event.target.closest('[data-quantity-input]');
    if (input) return this.#submitTyped(input, true);

    // Frequency select: '' takes the line off its subscription
    const plan = event.target.closest('[data-line-plan]');
    if (plan) {
      const line = plan.closest('[data-line-key]');
      this.change(line, { selling_plan: plan.value ? Number(plan.value) : null }, { kind: plan.value ? 'plan' : 'once', control: plan });
    }
  };

  // Enter in a quantity field updates the line rather than submitting the form (which goes to checkout)
  #onKeydown = (event) => {
    if (event.key === 'Enter' && event.target.matches?.('[data-quantity-input]')) {
      event.preventDefault();
      this.#submitTyped(event.target, true);
    }
  };

  #submitTyped(input, final = false) {
    const line = input.closest('[data-line-key]');
    if (!line) return;
    clearTimeout(this.#timers.get(line.id));
    this.#timers.delete(line.id);
    const current = Number(line.dataset.quantity);
    const raw = input.value.trim();
    // An empty or unreadable field is no change, not a removal; put the quantity back once the shopper leaves it
    if (raw === '' || !/^\d+$/.test(raw)) {
      if (final) {
        input.removeAttribute('data-skip-value-update');
        input.value = current;
      }
      return;
    }
    const quantity = Number(raw);
    if (quantity === current) {
      if (final) input.removeAttribute('data-skip-value-update');
      return;
    }
    this.change(line, { quantity }, { control: input });
  }

  /**
   * Changes one line through /cart/change.js (every cart view re-rendered in the same request). Changes to a line
   * already being updated wait for that request, then go with the line's new key (Shopify changes a line's key when
   * its quantity or plan changes); only the latest waiting change is sent.
   * @param {HTMLElement} line - a [data-line-key] element with a stable id
   * @param {{ quantity?: number, selling_plan?: number|null }} body
   * @param {{ kind?: 'plan'|'once'|'upgrade', control?: HTMLElement }} [options]
   */
  async change(line, body, options = {}) {
    if (!line) return;
    const lineId = line.id;
    if (this.#busy.has(lineId)) {
      this.#queued.set(lineId, { body, options });
      return;
    }
    this.#busy.add(lineId);

    const { kind } = options;
    const key = line.dataset.lineKey;
    const title = line.dataset.title || '';
    const position = [...this.querySelectorAll('[data-line-key]')].indexOf(line);
    const focused = document.activeElement;
    const hadFocus = this.contains(focused);
    const quantity = body.quantity ?? Number(line.dataset.quantity || 1);
    const removing = quantity === 0;

    line.setAttribute('aria-busy', 'true');
    this.#setLineError(line, '');

    try {
      const result = await announceCartChange(
        this,
        { action: removing ? 'remove' : 'update', lines: [{ id: key, quantity }], source: this.localName },
        () => cartRequest('cart/change.js', { id: key, quantity, ...body }, { signal: this.abortController.signal })
      );
      if (!this.render(result.sections[this.sectionId], result.sequence)) await this.refresh();
      // The field shows what the cart now holds (Shopify may have lowered it to the stock left), unless more typing is on the way
      const fresh = lineId && this.querySelector(`#${CSS.escape(lineId)}`);
      const input = fresh?.querySelector('[data-quantity-input]');
      if (input && !this.#queued.has(lineId) && !this.#timers.has(lineId)) {
        input.removeAttribute('data-skip-value-update');
        input.value = fresh.dataset.quantity;
      }
      this.#announce(this.#message({ title, kind, removing, quantity: Number(fresh?.dataset.quantity ?? quantity), empty: result.cart.item_count === 0 }));
      if (hadFocus) this.#restoreFocus({ focused, position, kind, removing });
      await this.#dropOrphanGifts(result.cart).catch((error) => console.warn('Cart:', error));
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.warn('Cart:', error);
      // The line as it was (the request failed, so nothing changed), with the reason underneath
      const current = (lineId && this.querySelector(`#${CSS.escape(lineId)}`)) || line;
      current.removeAttribute('aria-busy');
      const input = current.querySelector('[data-quantity-input]');
      if (input) {
        input.removeAttribute('data-skip-value-update');
        input.value = current.dataset.quantity;
      }
      current.querySelector('[data-upgrade-plan]')?.setAttribute('aria-checked', 'false');
      const plan = current.querySelector('[data-line-plan]');
      if (plan) plan.value = plan.querySelector('option[selected]')?.value ?? plan.value;
      this.#setLineError(current, error.message || GENERIC_ERROR);
    } finally {
      this.#busy.delete(lineId);
      const next = this.#queued.get(lineId);
      this.#queued.delete(lineId);
      const fresh = next && lineId && this.querySelector(`#${CSS.escape(lineId)}`);
      if (fresh) {
        if (next.body.quantity === undefined || next.body.quantity !== Number(fresh.dataset.quantity)) this.change(fresh, next.body, next.options);
        else fresh.querySelector('[data-quantity-input]')?.removeAttribute('data-skip-value-update');
      }
    }
  }

  // Free gifts come with a subscription: when the last subscription leaves the cart (removed or switched to buy
  // once), its gifts go too
  async #dropOrphanGifts(cart) {
    const items = cart?.items || [];
    if (items.some((item) => item.selling_plan_allocation)) return;
    const gifts = items.filter((item) => item.properties?._gift_with_subscription === 'true');
    if (!gifts.length) return;
    const updates = Object.fromEntries(gifts.map((item) => [item.key, 0]));
    const result = await announceCartChange(
      this,
      { action: 'remove', lines: gifts.map((item) => ({ id: item.key, quantity: 0 })), source: this.localName },
      () => cartRequest('cart/update.js', { updates }, { signal: this.abortController.signal })
    );
    if (!this.render(result.sections[this.sectionId], result.sequence)) await this.refresh();
    this.#announce('Free gifts removed: they come with a subscription.');
  }

  #setLineError(line, message) {
    const error = line.querySelector('[data-line-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #message({ title, kind, removing, quantity, empty }) {
    let message;
    if (removing) message = `Removed ${title}.`;
    else if (kind === 'upgrade') message = `${title} is now a subscription.`;
    else if (kind === 'plan') message = `${title} delivery frequency updated.`;
    else if (kind === 'once') message = `${title} is now a one-off purchase.`;
    else message = `${title} quantity updated to ${quantity}.`;
    if (empty) return `${message} Your cart is empty.`;
    const subtotal = this.querySelector('[data-cart-subtotal]');
    if (subtotal) message += ` ${subtotal.dataset.cartSubtotal || 'Subtotal'} ${subtotal.textContent.trim()}`;
    return message;
  }

  #announce(message) {
    const status = this.querySelector('[data-cart-status]');
    if (!status) return;
    status.textContent = '';
    // A fresh text node after a tick so the same message twice is still read out
    requestAnimationFrame(() => (status.textContent = message));
  }

  // Focus stays where it was when the control still exists (morph keeps it). Otherwise (line removed, or a plan
  // change gave the line a new key) it goes to the same place in the line now at that position, then the heading.
  #restoreFocus({ focused, position, kind, removing }) {
    // (Safari moves focus to the dialog, not the body, when the focused element goes)
    if (focused?.isConnected && document.activeElement === focused) return;
    const lines = [...this.querySelectorAll('[data-line-key]')];
    const line = lines[position] || lines[position - 1];
    let target = null;
    if (line && !removing && kind) target = line.querySelector('[data-line-plan], [data-upgrade-plan]');
    if (line && !target) target = line.querySelector('a[href]:not([tabindex="-1"]), button, select, input');
    if (!target) {
      // The heading takes focus only for this (tabindex is added here so opening the drawer doesn't focus it)
      target = this.querySelector('[data-cart-heading]');
      target?.setAttribute('tabindex', '-1');
    }
    if (!target) return;
    // After a tap, the moved focus is for screen readers only: no keyboard ring (phones drew a black box round the
    // new "Subscribe & Save" switch). Keyboard users still see it.
    if (!lastInputWasKeyboard) {
      target.setAttribute('data-focus-quiet', '');
      target.addEventListener('blur', () => target.removeAttribute('data-focus-quiet'), { once: true });
    }
    target.focus({ focusVisible: lastInputWasKeyboard });
  }
}

// Whether the last thing the shopper did was a key press (Tab, arrows) or a tap/click
let lastInputWasKeyboard = false;
if (!window.purolabsCartInputMode) {
  window.purolabsCartInputMode = true;
  document.addEventListener('keydown', (event) => {
    if (!event.metaKey && !event.altKey && !event.ctrlKey) lastInputWasKeyboard = true;
  }, true);
  document.addEventListener('pointerdown', () => (lastInputWasKeyboard = false), true);
}

/**
 * Recommended products loaded from Shopify's /recommendations/products (or any URL returning the section).
 * data-url: what to fetch; data-source: selector of the element in the response whose children are shown (defaults
 * to this element's tag); data-lazy: wait until the element (or, while it's hidden, its parent) nears the viewport.
 * data-pairings: also fetch the hand-picked pairings (Search & Discovery's complementary products, the same URL with
 * intent=complementary) and show them first, filled up from data-url's products. Each card carries
 * data-rec-product="<product id>" so repeats are dropped; data-limit caps the number of cards.
 * The section is hidden when the request fails or returns nothing. Responses are cached for five minutes; a newer load aborts an older one.
 */
export class Recommendations extends HTMLElement {
  static cache = new Map();
  static TTL = 5 * 60 * 1000;

  connectedCallback() {
    this.abortController = new AbortController();
    if (!this.dataset.url) return;
    if ('lazy' in this.dataset && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      // An element hidden until it has products (the cart drawer's) has no box and never intersects: watch its parent
      this.observer.observe(this.getClientRects().length ? this : this.parentElement || this);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.fetchController?.abort();
    this.abortController?.abort();
  }

  static async fetchHTML(url, { signal, fresh = false } = {}) {
    const cached = Recommendations.cache.get(url);
    if (!fresh && cached && Date.now() - cached.time < Recommendations.TTL) return cached.html;
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Recommendations failed (${response.status})`);
    const html = await response.text();
    Recommendations.cache.set(url, { html, time: Date.now() });
    return html;
  }

  /** Children of the matching element in the response, or [] */
  extract(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(this.dataset.source || this.localName);
    return source ? [...source.childNodes] : [];
  }

  /** The pairings' nodes with the other response's cards added after them, or whichever response has cards */
  combine(lists) {
    const [first, ...rest] = lists.filter((nodes) =>
      nodes.some((node) => node instanceof Element && (node.matches('[data-rec-product]') || node.querySelector('[data-rec-product]')))
    );
    if (!first) return lists.at(-1) || [];
    const fragment = document.createDocumentFragment();
    fragment.append(...first);
    const cards = () => [...fragment.querySelectorAll('[data-rec-product]')];
    const seen = new Set(cards().map((card) => card.dataset.recProduct));
    const parent = cards().at(-1).parentNode;
    for (const nodes of rest) {
      for (const node of nodes) {
        if (!(node instanceof Element)) continue;
        for (const card of node.matches('[data-rec-product]') ? [node] : node.querySelectorAll('[data-rec-product]')) {
          if (seen.has(card.dataset.recProduct)) continue;
          seen.add(card.dataset.recProduct);
          parent.append(card);
        }
      }
    }
    const limit = Number(this.dataset.limit);
    if (limit) cards().slice(limit).forEach((card) => card.remove());
    return [...fragment.childNodes];
  }

  /** Puts the fetched nodes in place; subclasses can filter them. Returns whether anything is shown. */
  show(nodes) {
    this.replaceChildren(...nodes);
    return this.children.length > 0;
  }

  async load({ fresh = false } = {}) {
    const url = this.dataset.url;
    if (!url) return;
    this.fetchController?.abort();
    const controller = new AbortController();
    this.fetchController = controller;
    try {
      const urls = [url];
      if ('pairings' in this.dataset) {
        const pairings = new URL(url, window.location.origin);
        pairings.searchParams.set('intent', 'complementary');
        urls.unshift(pairings.pathname + pairings.search);
      }
      // Either response is enough on its own
      const results = await Promise.allSettled(urls.map((each) => Recommendations.fetchHTML(each, { signal: controller.signal, fresh })));
      if (controller.signal.aborted) return;
      const htmls = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      if (!htmls.length) throw results[0].reason;
      this.loaded = true;
      this.hidden = !this.show(this.combine(htmls.map((html) => this.extract(html))));
    } catch (error) {
      if (controller.signal.aborted || error.name === 'AbortError') return;
      console.warn('Recommendations:', error);
      this.hidden = true;
    } finally {
      this.dataset.state = this.hidden ? 'empty' : 'loaded';
    }
  }
}
